import type { AssetId } from '../assets/AssetTypes';
/**
 * Gameplay perk of a selectable character: one clearly noticeable advantage and one disadvantage,
 * both described on the selection card. Applied at the start of every run through the existing
 * systems (ScoreManager, RunDirector, CollectibleMagnet). Jump height, lane change speed and the
 * other player physics are never touched – level generation validates sequences against them.
 */
export interface CharacterPerks {
  /** Name of the perk on the selection card. */
  readonly title: string;
  /** "✔ Vorteil" line of the card. */
  readonly advantage: string;
  /** "✖ Nachteil" line of the card. */
  readonly disadvantage: string;
  /** Extra lives on top of SCORING_CONFIG.startExtraLives (hearts filled from the start). */
  readonly startExtraLives: number;
  /** Multiplies SCORING_CONFIG.bottlesPerExtraLife (1.15 = 15 % more bottles per extra life). */
  readonly bottlesPerExtraLifeFactor: number;
  /**
   * Permanent bottle magnet radius (m, 0 = none). It leaves the runner's own lane to the normal pickup
   * and pulls only from neighbouring lanes. A magnet power-up (all lanes, no limit) works on top.
   */
  readonly magnetRadius: number;
  /** Bottles the permanent magnet pulls per bottle row of a neighbouring lane (the first ones it reaches). */
  readonly neighbourLaneBottleLimit: number;
  /** A stumble also costs one collected extra life (if there is one) – visible on the hearts. */
  readonly stumbleCostsExtraLife: boolean;
}

/** Sound cues of the extra-life drink (clip frames, see CharacterConfig.drinkBreak). */
export interface DrinkCues {
  readonly open: number;
  readonly gulp: readonly number[];
  readonly ahh: number;
  readonly prost: number;
}
export type DrinkCue = keyof DrinkCues;

/**
 * Player character selection, character perks and purely visual animation tuning.
 */
export interface CharacterConfig {
  /** Default character: first start, or a stored choice that is no longer selectable. */
  readonly selected: string;
  /** Characters offered on the start / game-over screen, in card order (each needs `perks`). */
  readonly selectable: readonly string[];
  /** Gameplay perks by character id – characters without an entry play neutral. */
  readonly perks: Readonly<Record<string, CharacterPerks>>;
  /**
   * Extra life = "Prost!": the runner stops and drinks (ANIM_Drink) while the world waits (DrinkBreak).
   * clipFrames / bottleFrames must match DRINK_FRAMES / DRINK_BOTTLE in tools/blender/lib/runner_animations.py
   * (checked by a unit test).
   */
  readonly drinkBreak: {
    /** Last key frame of ANIM_Drink (the clip runs from frame 1). */
    readonly clipFrames: number;
    /** Frame rate of the clips (character_kit.FPS). */
    readonly clipFps: number;
    /** Clip frames the bottle pops into the hand / away again (DRINK_BOTTLE). */
    readonly bottleFrames: readonly [number, number];
    /**
     * The bottle: the game's collectible model hung on the rig's hand anchor (PROP_BottleAnchor, hand_prop.py).
     * It pops in (grows past full size by `overshoot`, settles) and pops away (shrinks to nothing).
     */
    readonly bottle: {
      readonly assetId: AssetId;
      readonly popInSeconds: number;
      readonly popOutSeconds: number;
      readonly overshoot: number;
    };
    /** Clip frames of the sounds: bottle opened, each gulp, "Ahh", clinking "Prost!" (DRINK_CUES). */
    readonly cues: DrinkCues;
    /** A drink waits this long for the runner to be on a free stretch (see below), then it is dropped. */
    readonly pendingSeconds: number;
    /**
     * The drink only starts on a free stretch – the run goes on from there after the break: this long at the
     * current speed (at least `minClearMeters`) without an obstacle in the runner's lane (± `laneMargin` beside
     * its body), a moving hazard, a junction decision, a ground edge (pit, ramp, platform; `groundSamples`
     * checks) or anything overhead.
     */
    readonly clearAheadSeconds: number;
    readonly minClearMeters: number;
    readonly laneMargin: number;
    readonly groundSamples: number;
    /** Lateral speed (m/s) below which the runner counts as standing in a lane – the drink may start. */
    readonly settledLateralSpeed: number;
  };
  /** Cross-fade between clips (seconds). */
  readonly fadeSeconds: number;
  /** Shorter cross-fade for snappy reactions (jump, slide, hit). */
  readonly quickFadeSeconds: number;
  /**
   * Run speed (m/s) at which ANIM_Run plays at its authored speed – the stance foot is planted at exactly
   * this speed (RUN in tools/blender/lib/runner_animations.py, checked by a unit test).
   */
  readonly runClipSpeed: number;
  /** Run speed (m/s) at which ANIM_RunFast plays at its authored speed (RUN_FAST in runner_animations.py). */
  readonly runFastClipSpeed: number;
  /** Normalized difficulty speed (0..1) from which the fast run cycle is used. */
  readonly runFastFrom: number;
  /**
   * Playback speed limits of the run cycles (no slow-motion / no blur). Within them the feet stay planted;
   * together with runFastFrom they cover the whole speed range (unit test).
   */
  readonly runSpeedRatio: { readonly min: number; readonly max: number };
  /**
   * How far a one-shot reaction pushes the base clip back on the bones it drives (0..1). At full
   * reaction weight the base keeps 1 − dominance; Babylon normalizes the sum, so 0.85 gives the
   * reaction ≈ 87 % and leaves a little of the run motion alive underneath.
   */
  readonly overlayDominance: number;
  /** Upper-body pickup gesture at most this often (seconds) – bottles come in rows. */
  readonly collectCooldownSeconds: number;
  /** Lean into lane changes of rigged characters (deg per m/s lateral speed, capped). */
  readonly leanDegreesPerLateralSpeed: number;
  readonly maxLeanDegrees: number;
  /**
   * Cheering (ANIM_Celebrate as upper-body fist pump): on an extra life, on bonus pickups and at
   * distance milestones. A cheer waits up to `pendingSeconds` for the runner to be back on the ground.
   */
  readonly cheer: {
    readonly cooldownSeconds: number;
    readonly bonusFromBottleValue: number;
    readonly milestoneMeters: number;
    readonly pendingSeconds: number;
  };
  /**
   * Bones (without instance prefix) driven by layered one-shots – everything else keeps playing the
   * base clip: lane changes only lean the torso, the pickup only uses the right arm, the cheer both arms.
   */
  readonly clipMasks: {
    readonly LaneLeft: readonly string[];
    readonly LaneRight: readonly string[];
    readonly Collect: readonly string[];
    readonly Celebrate: readonly string[];
  };
}

const TORSO = ['Spine', 'Chest', 'UpperChest', 'Neck', 'Head'];
const RIGHT_ARM = ['Clavicle_R', 'UpperArm_R', 'LowerArm_R', 'Hand_R', 'Fingers_01_R', 'Fingers_02_R', 'Thumb_01_R', 'Thumb_02_R'];
const LEFT_ARM = RIGHT_ARM.map((bone) => bone.replace(/_R$/, '_L'));
/** Non-breaking space: a number stays on one line with its unit / noun on the cards ("30 %", "3 Flaschen"). */
const NBSP = '\u00a0';

export const CHARACTER_CONFIG: CharacterConfig = {
  selected: 'CHAR_02_CARPENTER_M',
  selectable: ['CHAR_02_CARPENTER_M', 'CHAR_01_ELECTRICIAN_F'],
  perks: {
    CHAR_02_CARPENTER_M: {
      title: 'Bärenstark',
      advantage: `Startet mit 1${NBSP}Extra-Leben.`,
      disadvantage: `Schwer beladen: braucht 15${NBSP}% mehr Flaschen für das nächste Extra-Leben.`,
      startExtraLives: 1,
      bottlesPerExtraLifeFactor: 1.15, // 200 → 230 bottles per extra life (user 2026-10-03: 30 % → 15 %)
      magnetRadius: 0,
      neighbourLaneBottleLimit: 0,
      stumbleCostsExtraLife: false,
    },
    CHAR_01_ELECTRICIAN_F: {
      title: 'Immer unter Strom',
      advantage: `Flaschen-Magnet: Zieht die ersten 3${NBSP}Flaschen jeder Reihe aus der Nachbarspur an.`,
      disadvantage: 'Leichtgewicht: Jeder Stolperer kostet sie ein gesammeltes Extra-Leben.',
      startExtraLives: 0,
      bottlesPerExtraLifeFactor: 1,
      // Neighbour lane (2.2 m) yes, two lanes away (4.4 m) never – checked by a unit test.
      magnetRadius: 3,
      neighbourLaneBottleLimit: 3, // the rest of the row needs a lane change
      stumbleCostsExtraLife: true, // the magnet brings lives faster, every stumble takes one again
    },
  },
  drinkBreak: {
    clipFrames: 90,
    clipFps: 30,
    bottleFrames: [14, 80],
    bottle: { assetId: 'COL_BEER_BOTTLE_A', popInSeconds: 0.22, popOutSeconds: 0.16, overshoot: 0.25 },
    cues: { open: 14, gulp: [34, 41, 48, 55], ahh: 62, prost: 72 },
    pendingSeconds: 6,
    clearAheadSeconds: 1.5,
    minClearMeters: 14,
    laneMargin: 0.6,
    groundSamples: 8,
    settledLateralSpeed: 0.05,
  },
  fadeSeconds: 0.18,
  quickFadeSeconds: 0.08,
  runClipSpeed: 8,
  runFastClipSpeed: 14,
  runFastFrom: 0.3,
  runSpeedRatio: { min: 0.75, max: 1.4 },
  overlayDominance: 0.85,
  collectCooldownSeconds: 1.2,
  leanDegreesPerLateralSpeed: 0.8,
  maxLeanDegrees: 6,
  cheer: { cooldownSeconds: 4, bonusFromBottleValue: 5, milestoneMeters: 1000, pendingSeconds: 1.5 },
  clipMasks: {
    LaneLeft: TORSO,
    LaneRight: TORSO,
    Collect: [...TORSO, ...RIGHT_ARM],
    Celebrate: [...TORSO, ...RIGHT_ARM, ...LEFT_ARM],
  },
};
